import { useState, useEffect, useReducer, useRef } from "react";
import consumer from "channels/consumer";
import ReactHtmlParser from "react-html-parser";
import { LOADING_STATE } from "components/ai_tools/ChatBubble";
import LcFetch from "lib/shared/fetch";
import { logAiEvent } from "lib/shared/events";
import { AI_TOOL_NAMES, AI_TOOLS_ERROR_MESSAGES } from "../constants";
import { useAccessTier } from "../../../contexts/AccessTierContext";
import messagesReducer, {
  ADD_LOADING_MESSAGE,
  ADD_REMINDER,
  ADD_UPGRADE_MESSAGE,
  ADD_USER_MESSAGE,
  ERROR_LAST_MESSAGE,
  FINALIZE_MESSAGE,
  PREPEND_MESSAGES,
  RESET_TO_INITIAL,
  UPDATE_STREAMING_MESSAGE
} from "../reducers/messagesReducer";

const useChatMessages = (props, dependencies = []) => {
  const {
    aiAnswersCompletePath,
    channel,
    eventCategory,
    inContent,
    locked,
    messageText,
    quotaFromProps,
    onQuotaChange,
    resourceTitle,
    signupPath,
    submissionPath,
    toolName,
    upgradeable,
    infiniteAnswers
  } = props;
  // The order matters for these state initializers
  const [isStreaming, setIsStreaming] = useState(false);
  const [loadingPastMessages, setLoadingPastMessages] = useState(false);
  const [quotaRemaining, setQuotaRemaining] = useState(quotaFromProps);
  const [messages, dispatch] = useReducer(messagesReducer, []);
  const [prevSlug, setPrevSlug] = useState();

  const cursor = useRef();
  const hasMore = useRef(true);
  const messagesCallbacks = useRef([]);
  const { isSignedOut, isFree } = useAccessTier();
  const canAskQuestions =
    infiniteAnswers || (!isSignedOut && quotaRemaining > 0);
  const showFreeTierUpgradeMessage =
    isFree && quotaRemaining === 0 && toolName === AI_TOOL_NAMES.ASK_AI;

  const handleError = () => {
    dispatch({
      payload: AI_TOOLS_ERROR_MESSAGES[toolName],
      type: ERROR_LAST_MESSAGE
    });
    setIsStreaming(false);
  };

  const handleRedirect = (json) => {
    if (json?.redirect_to) {
      window.location.replace(json.redirect_to);
    }
  };

  const handleAskAIFormSubmit = ({ question, title: slug }) => {
    setIsStreaming(true);
    messagesCallbacks.current.push(() => {
      dispatch({ type: ADD_LOADING_MESSAGE });
    });
    messagesCallbacks.current.push(() => {
      LcFetch(submissionPath, {
        body: JSON.stringify({
          question,
          resource: slug,
          tool_name: toolName
        }),
        method: "POST"
      })
        .then((response) => {
          if (!response.ok) {
            handleError();
          } else {
            return response.json();
          }
        })
        .then(handleRedirect)
        .catch(handleFetchError);
    });

    setPrevSlug(slug);
    dispatch({ payload: question, type: ADD_USER_MESSAGE });
  };

  const handleQuizDiscussionEssayPrompt = ({
    topic,
    subtopic,
    messageText: inputMessageText,
    title: slug
  }) => {
    setIsStreaming(true);
    messagesCallbacks.current.push(() => {
      dispatch({ type: ADD_LOADING_MESSAGE });
    });

    messagesCallbacks.current.push(() => {
      const requestBody = {
        resource: slug,
        subtopic,
        tool_name: toolName,
        topic
      };

      if (toolName === AI_TOOL_NAMES.DISCUSSION_QUESTIONS) {
        requestBody.question = inputMessageText ?? messageText;
      }

      LcFetch(submissionPath, {
        body: JSON.stringify(requestBody),
        method: "POST"
      })
        .then((response) => {
          // we're logged out somehow, like in another tab
          if (response.status === 401) {
            window.location.href = signupPath;
            return;
          }

          if (!response.ok) {
            handleError();
          } else {
            return response.json();
          }
        })
        .then(handleRedirect)
        .catch(handleFetchError);
    });

    setPrevSlug(slug);
    dispatch({
      payload: ReactHtmlParser(inputMessageText ?? messageText),
      type: ADD_USER_MESSAGE
    });
  };

  const handleBroadcastReceived = (data) => {
    if (data.stop) {
      setIsStreaming(false);
      dispatch({
        payload: { aiAnswerId: data.ai_answer_id, body: data.body },
        type: FINALIZE_MESSAGE
      });

      if (
        toolName !== AI_TOOL_NAMES.ASK_AI &&
        toolName !== AI_TOOL_NAMES.DISCUSSION_QUESTIONS
      ) {
        messagesCallbacks.current.push(() => {
          dispatch({ type: ADD_REMINDER });
        });
      }

      // Use currentQuota directly to ensure we have the current value
      setQuotaRemaining((currentQuota) => {
        const newQuota = currentQuota - 1;
        if (onQuotaChange) {
          onQuotaChange(newQuota);
        }

        // Get the new quota value for the UI check
        if (newQuota === 0 && isFree && toolName === AI_TOOL_NAMES.ASK_AI) {
          dispatch({
            payload: {
              amplitudeKey: inContent
                ? "aiContentToolUpgradeFromAlert"
                : "standaloneUpgradeFromAlert",
              signupPath
            },
            type: ADD_UPGRADE_MESSAGE
          });
        }
        return newQuota;
      });
      if (data.ai_answer_id) {
        LcFetch(aiAnswersCompletePath, {
          body: JSON.stringify({
            ai_answer_id: data.ai_answer_id
          }),
          method: "POST"
        })
          .then((response) => {
            if (!response.ok) {
              handleError();
            }
          })
          .catch(handleFetchError);
      }
      logAiEvent("AI Response Completed", eventCategory);
    } else if (data.error) {
      handleError();
    } else {
      const previousMessage = messages[messages.length - 1];

      if (previousMessage?.streamState === LOADING_STATE) {
        logAiEvent("AI Response Started", eventCategory);
      }

      dispatch({
        payload: { aiAnswerId: data.ai_answer_id, body: data.body },
        type: UPDATE_STREAMING_MESSAGE
      });
    }
  };

  const fetchAiAnswers = async () => {
    if (isSignedOut || !hasMore.current || isStreaming || loadingPastMessages)
      return;

    setLoadingPastMessages(true);

    const cursorParam = cursor.current ? `&cursor=${cursor.current}` : "";
    const toolNameParam = toolName.toLowerCase();
    const response = await LcFetch(
      `/ai_answers?tool_name=${toolNameParam}${cursorParam}`
    );

    const data = await response.json();

    const hasResults = data.aiAnswers.length > 0;

    if (hasResults) {
      dispatch({ payload: data.aiAnswers, type: PREPEND_MESSAGES });
      if (showFreeTierUpgradeMessage) {
        dispatch({
          payload: {
            amplitudeKey: inContent
              ? "aiContentToolUpgradeFromAlert"
              : "standaloneUpgradeFromAlert",
            signupPath
          },
          type: ADD_UPGRADE_MESSAGE
        });
      }

      cursor.current = data.nextCursor;
      hasMore.current = !!data.hasMore;
    } else {
      hasMore.current = false;
    }

    setLoadingPastMessages(false);
    return hasResults;
  };

  useEffect(() => {
    if (locked) return;
    // Connect to the channel

    consumer.subscriptions.create(
      {
        channel
      },
      {
        connected: () => console.log("connected"),
        disconnected: () => console.log("disconnected"),
        received: handleBroadcastReceived
      }
    );

    return () => {
      consumer.disconnect();
    };
  }, []);

  useEffect(async () => {
    if (messages.length > 1) return;
    // Fetching answer should only happen for the ASK AI and for the DQG In-Content Tool
    if (
      (toolName === AI_TOOL_NAMES.ASK_AI ||
        toolName === AI_TOOL_NAMES.DISCUSSION_QUESTIONS) &&
      !isSignedOut
    ) {
      const hasResults = await fetchAiAnswers();
      if (hasResults) return;
    }

    hasMore.current = false;
    dispatch({
      payload: {
        inContent,
        locked,
        quotaRemaining,
        resourceTitle,
        signupPath,
        toolName,
        upgradeable
      },
      type: RESET_TO_INITIAL
    });
  }, [...dependencies]);

  useEffect(() => {
    messagesCallbacks.current.forEach((callback) => {
      setTimeout(callback, 500);
    });
    messagesCallbacks.current = [];
  }, [messages]);

  const handleFetchError = () => {
    // This block is executed if there is a network error or if the request fails
    // The catch block captures exceptions thrown during the fetch operation,
    // which could occur if the client is offline or if there are network connectivity issues.
    handleError();
  };

  return {
    canAskQuestions,
    canFetchMoreAnswers: hasMore.current,
    fetchAiAnswers,
    handleAskAIFormSubmit,
    handleQuizDiscussionEssayPrompt,
    isStreaming,
    loadingPastMessages,
    messages,
    prevSlug,
    quotaRemaining
  };
};

export default useChatMessages;
